import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const Gallery = () => {
  const [terms, setTerms] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Fetch categories (terms) on mount
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/taxonomies/gallerikategori/terms');
        if (res.ok) {
          const data = await res.json();
          setTerms(data);
        }
      } catch (err) {
        console.error('Fejl ved hentning af kategorier:', err);
      }
    };
    fetchTerms();
  }, []);

  // Fetch gallery items when category or offset changes
  useEffect(() => {
    const fetchItems = async () => {
      if (offset === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        let url = `https://api.dev.auxo.dk/public/v2/content/gallerier?limit=8&offset=${offset}`;
        if (selectedCategory !== 'Alle') {
          url += `&term=gallerikategori:${selectedCategory}`;
        }

        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          if (offset === 0) {
            setItems(data.items || []);
          } else {
            setItems(prev => [...prev, ...(data.items || [])]);
          }
          setTotal(data.total || 0);
        }
      } catch (err) {
        console.error('Fejl ved hentning af galleribilleder:', err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchItems();
  }, [selectedCategory, offset]);

  // Reset offset when category changes
  const handleCategoryChange = (categorySlug) => {
    setSelectedCategory(categorySlug);
    setOffset(0);
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        navigateLightbox(1);
      } else if (e.key === 'ArrowLeft') {
        navigateLightbox(-1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, items]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    document.body.style.overflow = ''; // Restore scrolling
  };

  const navigateLightbox = (direction) => {
    if (lightboxIndex === null) return;
    let newIndex = lightboxIndex + direction;
    if (newIndex >= items.length) {
      newIndex = 0;
    } else if (newIndex < 0) {
      newIndex = items.length - 1;
    }
    setLightboxIndex(newIndex);
  };

  const loadMore = () => {
    if (items.length < total) {
      setOffset(prev => prev + 8);
    }
  };

  // Helper to get category label for an item
  const getCategoryLabel = (item) => {
    const slug = item.terms?.['gallerikategori']?.[0];
    if (!slug) return '';
    const term = terms.find(t => t.slug === slug);
    return term ? term.label : slug;
  };

  return (
    <section id="galleri" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-orange-600 font-semibold tracking-wide uppercase text-xs mb-2">Galleri</h2>
          <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Udført brolæggerarbejde</h3>
          <div className="w-16 h-1 bg-orange-600 mx-auto rounded-full mb-4"></div>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Gå på opdagelse i mine tidligere projekter. Alle billeder er fra rigtige opgaver udført af Nielsen Brolægning, så du kan se den høje kvalitet og finish med egne øjne.
          </p>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          <button
            onClick={() => handleCategoryChange('Alle')}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
              selectedCategory === 'Alle'
                ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/20'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Alle
          </button>
          {terms.map((term) => (
            <button
              key={term.slug}
              onClick={() => handleCategoryChange(term.slug)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                selectedCategory === term.slug
                  ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {term.label}
            </button>
          ))}
        </div>

        {/* Image Grid */}
        {loading && items.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-slate-100 rounded-2xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map((item, index) => (
              <div
                key={item.id}
                onClick={() => openLightbox(index)}
                className="group relative aspect-[4/3] rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 cursor-pointer bg-slate-100"
              >
                <img
                  src={item.image?.[0]}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-6">
                  <div className="self-end bg-white/20 backdrop-blur-md p-2 rounded-lg text-white">
                    <ZoomIn className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="inline-block bg-orange-600 text-white text-xs font-bold px-2.5 py-1 rounded-full mb-2">
                      {getCategoryLabel(item)}
                    </span>
                    <h4 className="text-white font-bold text-lg leading-tight line-clamp-2">
                      {item.title}
                    </h4>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && items.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500 text-lg">Ingen billeder fundet i denne kategori.</p>
          </div>
        )}

        {/* Load More Button */}
        {!loading && items.length < total && (
          <div className="text-center mt-12">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="px-8 py-4 bg-slate-900 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? 'Henter...' : `Vis flere billeder (${total - items.length} tilbage)`}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && items[lightboxIndex] && (
        <div className="fixed inset-0 z-50 bg-slate-950/95 flex flex-col justify-between p-4 md:p-8 select-none">
          {/* Top Bar */}
          <div className="flex justify-between items-center text-white w-full max-w-7xl mx-auto">
            <div className="text-sm font-medium text-slate-400">
              Billede {lightboxIndex + 1} af {items.length} — {getCategoryLabel(items[lightboxIndex])}
            </div>
            <button
              onClick={closeLightbox}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-white"
              aria-label="Luk galleri"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Main Content (Image & Navigation) */}
          <div className="relative flex items-center justify-center flex-grow max-w-7xl mx-auto w-full my-4">
            {/* Prev Button */}
            <button
              onClick={() => navigateLightbox(-1)}
              className="absolute left-0 md:left-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Image */}
            <div className="max-h-[70vh] md:max-h-[80vh] max-w-full flex items-center justify-center">
              <img
                src={items[lightboxIndex].image?.[0]}
                alt={items[lightboxIndex].title}
                className="max-h-[70vh] md:max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Next Button */}
            <button
              onClick={() => navigateLightbox(1)}
              className="absolute right-0 md:right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              aria-label="Næste billede"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Bottom Bar (Caption) */}
          <div className="text-center text-white w-full max-w-3xl mx-auto pb-4">
            <h4 className="text-xl font-bold mb-1">
              {items[lightboxIndex].title}
            </h4>
            <p className="text-sm text-slate-400">
              Udført af Nielsen Brolægning
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
